/**
 * WHO the request-submission form is being filled FOR, as an injectable data source.
 *
 * Owner 2026-09-05: the admin Processes module had no way to file a request for a worker. The BACKEND
 * capability shipped first (alembic 120 records who typed it; `can_worker_initiate_definition` still runs
 * against the requester), and the UI blocked on this: the server validates the submitted `data` against
 * the definition's form schema, so a dialog that cannot RENDER that schema would 400 on any definition
 * with a required field — a picker whose options are refused is worse than no picker (svdy-ui §19).
 *
 * ── WHY A DATA SOURCE AND NOT A COPY OF THE FORM ────────────────────────────────────────────────
 *
 * `WorkerSubmitRequest.tsx` is 1232 lines and its field rendering is worth reusing exactly. A census of
 * its session coupling found the surface is far smaller than the file: EIGHT api calls and THREE reads of
 * the signed-in profile. Everything else is schema-driven and already indifferent to whose request it is.
 *
 * So nothing is rewritten. The eight calls move behind this interface, the worker portal passes the
 * session-scoped implementation it already had, and an admin passes one bound to a chosen worker. That is
 * the same shape as the licence declaration extracted on 2026-09-04: one place decides, two consumers
 * read (svdy-ui §17).
 *
 * ── WHAT MUST BE PER-SUBJECT, AND WHY EACH ─────────────────────────────────────────────────────
 *
 *   availableProcesses   `can_worker_initiate_definition` is evaluated against the REQUESTER, so the
 *                        admin's own list would offer definitions the target cannot start
 *   profile              auto-fills the requester's name and generates the title — the SUBJECT's name
 *                        belongs there, never the filer's
 *   businessDays         a leave span is measured against the subject's own calendar and schedule
 *   locations/departments org-wide today, but read through the interface so a future per-worker scope
 *                        (a branch-restricted admin) has one place to change
 *   requests.get         re-submission reads the ORIGINAL request, whoever filed it
 *   requests.create      the worker path posts to `/me/requests`; the admin path posts to
 *                        `/processes/requests` with `requester_id`, and the server records `created_by`
 */
import { meApi, organizationApi, processesApi } from '../../api';
import type { MeProfile } from '../../api/me';


/**
 * The subject's profile, as `MeProfile` — the shape the form already consumes.
 *
 * Typed as the full interface rather than the three fields I expected it to read: the form turned out to
 * touch more of it, and narrowing pushed a cast onto the call site, which is the opposite of what
 * extracting this was for. The admin path MAPS its `WorkerResponse` into this shape once, here, so the
 * 1232-line form stays indifferent to who it is filling for.
 */
export type SubmitRequestProfile = MeProfile;

export interface SubmitRequestSubject {
  /** The worker whose request this IS. `null` means "the signed-in worker" (the worker portal). */
  workerId: string | null;
  /** True when someone else is filing it — drives the "filed on behalf" copy and the POST target. */
  onBehalf: boolean;
}

export interface SubmitRequestDataSource {
  subject: SubmitRequestSubject;
  /** Definitions the SUBJECT may start. */
  listAvailableProcesses: () => ReturnType<typeof meApi.availableProcesses.list>;
  /** The subject's profile, best-effort — a network blip must not block the picker. */
  getProfile: () => Promise<SubmitRequestProfile | null>;
  listActiveLocations: () => ReturnType<typeof meApi.organizationLocations.listActive>;
  listActiveDepartments: () => ReturnType<typeof meApi.departments.listActive>;
  /** Business-day count for a span, measured against the SUBJECT's calendar. */
  getBusinessDays: (start: string, end: string, halfDay: boolean) => ReturnType<typeof meApi.businessDays.get>;
  getRequest: (id: string) => ReturnType<typeof meApi.requests.get>;
  /** Shaped exactly like `meApi.requests.create`'s argument, so the worker path is the identity
   *  implementation and nothing about the existing call had to change. */
  createRequest: (body: {
    process_definition_id?: string | null;
    title: string;
    priority?: string;
    form_data?: Record<string, unknown> | null;
    cc_worker_ids?: string[] | null;
  }) => ReturnType<typeof meApi.requests.create>;
  resubmitRequest: (id: string, body: Record<string, unknown>) => ReturnType<typeof meApi.requests.resubmit>;
}

/**
 * The worker portal's own source: everything scoped to the signed-in session, which is what the eight
 * calls did inline before. Behaviour is unchanged — this is the identity implementation.
 */
export function ownRequestDataSource(): SubmitRequestDataSource {
  return {
    subject: { workerId: null, onBehalf: false },
    listAvailableProcesses: () => meApi.availableProcesses.list(),
    getProfile: () => meApi.getProfile().catch(() => null),
    listActiveLocations: () => meApi.organizationLocations.listActive(),
    listActiveDepartments: () => meApi.departments.listActive(),
    getBusinessDays: (start, end, halfDay) => meApi.businessDays.get(start, end, halfDay),
    getRequest: (id) => meApi.requests.get(id),
    createRequest: (body) => meApi.requests.create(body),
    resubmitRequest: (id, body) => meApi.requests.resubmit(id, body),
  };
}

/**
 * An admin filing FOR a worker.
 *
 * `listAvailableProcesses` hits the endpoint added with the capability, because the admin's own list
 * would offer refused options. `createRequest` posts to the admin route with `requester_id`, and the
 * server records the admin as `created_by` and answers `filed_on_behalf` — so the approval history says
 * who typed it, which is the whole reason the refusal could be lifted.
 *
 * `getProfile` returns the SUBJECT's profile, never the admin's: the auto-filled name on the form is the
 * requester's.
 */
export function onBehalfRequestDataSource(workerId: string): SubmitRequestDataSource {
  return {
    subject: { workerId, onBehalf: true },
    listAvailableProcesses: () => processesApi.processRequestsApi.availableFor(workerId),
    // The SUBJECT's profile, from the admin-side worker record — `meApi.getProfile` is the SESSION's and
    // would auto-fill the admin's own name onto the requester's form.
    getProfile: async () => {
      const w = await organizationApi.workersApi.get(workerId).catch(() => null);
      // One mapping, in one place. `MeProfile` is the session shape and a `WorkerResponse` is the admin
      // shape; the form reads the former, so the translation belongs to the data source rather than to
      // every field that renders a name.
      return w ? ({ ...w } as unknown as SubmitRequestProfile) : null;
    },
    listActiveLocations: () => meApi.organizationLocations.listActive(),
    listActiveDepartments: () => meApi.departments.listActive(),
    getBusinessDays: (start, end, halfDay) => meApi.businessDays.get(start, end, halfDay),
    getRequest: (id) => meApi.requests.get(id),
    createRequest: (body) => processesApi.processRequestsApi.create({
      requester_id: workerId,
      // The admin route REQUIRES a definition — the engine instantiates one, not a bare title — and the
      // picker only ever offers definitions, so this cannot be empty in practice. Asserted rather than
      // defaulted, because a silent '' would reach the server as a validation error the user cannot read.
      definition_id: body.process_definition_id ?? '',
      title: body.title,
      priority: body.priority,
      data: body.form_data ?? {},
    }),
    // Re-submission is a WORKER action on their own rejected request — an admin does not re-submit
    // someone else's, they file a new one. Refused loudly rather than silently doing the wrong thing.
    resubmitRequest: () => Promise.reject(
      new Error('Re-submission belongs to the requester; file a new request instead.'),
    ),
  };
}
